import React, { useState, useMemo } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { RadioButton } from 'primereact/radiobutton';
import { DocumentSummary } from '@/api/domains/documents/types';
import styles from './DocumentSelector.module.css';

interface DocumentSelectorProps {
  documents: DocumentSummary[];
  selectedDocumentId: string | null;
  onSelect: (sourceUrl: string) => void;
  excludeDocumentId?: string | null;
  title: string;
  description?: string;
  showSimilarityScores?: boolean;
  similarityScores?: Map<string, number>;
  emptyMessage?: string;
}

type SortOption = 'name' | 'date' | 'language' | 'similarity';
type SortDirection = 'asc' | 'desc';

export const DocumentSelector: React.FC<DocumentSelectorProps> = ({
  documents,
  selectedDocumentId,
  onSelect,
  excludeDocumentId,
  title,
  description,
  showSimilarityScores = false,
  similarityScores,
  emptyMessage = 'No documents available'
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>(
    showSimilarityScores ? 'similarity' : 'name'
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    showSimilarityScores ? 'desc' : 'asc'
  );
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');

  const sortOptions = [
    { label: 'Name', value: 'name' as SortOption },
    { label: 'Date', value: 'date' as SortOption },
    { label: 'Language', value: 'language' as SortOption },
    ...(showSimilarityScores
      ? [{ label: 'Similarity', value: 'similarity' as SortOption }]
      : [])
  ];

  const filteredAndSortedDocuments = useMemo(() => {
    let filtered = documents.filter((doc) => {
      if (excludeDocumentId && doc.source_url === excludeDocumentId) {
        return false;
      }

      if (!searchQuery.trim()) {
        return true;
      }

      const query = searchQuery.toLowerCase();
      return (
        doc.title.toLowerCase().includes(query) ||
        doc.source_url.toLowerCase().includes(query) ||
        doc.language.toLowerCase().includes(query)
      );
    });

    if (!showSimilarityScores || !similarityScores) {
      filtered.sort((a, b) => {
        let compareValue = 0;

        switch (sortBy) {
          case 'name':
            compareValue = a.title.localeCompare(b.title);
            break;
          case 'date':
            compareValue =
              new Date(a.published_date).getTime() -
              new Date(b.published_date).getTime();
            break;
          case 'language':
            compareValue = a.language.localeCompare(b.language);
            break;
        }

        return sortDirection === 'asc' ? compareValue : -compareValue;
      });
    }

    return filtered;
  }, [
    documents,
    excludeDocumentId,
    searchQuery,
    sortBy,
    sortDirection,
    showSimilarityScores,
    similarityScores
  ]);

  const { recommendedDocs, otherDocs } = useMemo(() => {
    if (!showSimilarityScores || !similarityScores) {
      return { recommendedDocs: [], otherDocs: filteredAndSortedDocuments };
    }

    const recommended = filteredAndSortedDocuments.filter((doc) => {
      const sim = similarityScores.get(doc.source_url) || 0;
      return sim >= 0.6;
    });

    const others = filteredAndSortedDocuments.filter((doc) => {
      const sim = similarityScores.get(doc.source_url) || 0;
      return sim < 0.6;
    });

    recommended.sort((a, b) => {
      const simA = similarityScores.get(a.source_url) || 0;
      const simB = similarityScores.get(b.source_url) || 0;
      return simB - simA; // Descending order
    });

    others.sort((a, b) => {
      let compareValue = 0;

      switch (sortBy) {
        case 'name':
          compareValue = a.title.localeCompare(b.title);
          break;
        case 'date':
          compareValue =
            new Date(a.published_date).getTime() -
            new Date(b.published_date).getTime();
          break;
        case 'language':
          compareValue = a.language.localeCompare(b.language);
          break;
        case 'similarity':
          const simA = similarityScores.get(a.source_url) || 0;
          const simB = similarityScores.get(b.source_url) || 0;
          compareValue = simB - simA;
          break;
      }

      return sortDirection === 'asc' ? compareValue : -compareValue;
    });

    return { recommendedDocs: recommended, otherDocs: others };
  }, [
    filteredAndSortedDocuments,
    showSimilarityScores,
    similarityScores,
    sortBy,
    sortDirection
  ]);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getLanguageFlag = (language: string): string => {
    const flags: Record<string, string> = {
      en: '🇬🇧',
      de: '🇩🇪',
      fr: '🇫🇷',
      es: '🇪🇸',
      it: '🇮🇹',
      pt: '🇵🇹',
      nl: '🇳🇱',
      pl: '🇵🇱'
    };
    return flags[language.toLowerCase()] || '🌐';
  };

  const getLanguageLabel = (language: string): string => {
    const labels: Record<string, string> = {
      en: 'English',
      de: 'German',
      fr: 'French',
      es: 'Spanish',
      it: 'Italian',
      pt: 'Portuguese',
      nl: 'Dutch',
      pl: 'Polish'
    };
    return labels[language.toLowerCase()] || language.toUpperCase();
  };

  const getSimilarityBadge = (similarity: number) => {
    if (similarity >= 0.8) {
      return { label: 'Recommended', severity: 'success' as const };
    }
    if (similarity >= 0.6) {
      return { label: 'Recommended', severity: 'info' as const };
    }
    return null;
  };

  const renderDocument = (doc: DocumentSummary) => {
    const isSelected = selectedDocumentId === doc.source_url;
    const similarity = similarityScores?.get(doc.source_url);
    const badge = similarity ? getSimilarityBadge(similarity) : null;

    return (
      <div
        key={doc.source_url}
        className={`${styles.documentCard} ${
          isSelected ? styles.documentCardSelected : ''
        } ${badge ? styles.documentCardRecommended : ''}`}
        onClick={() => onSelect(doc.source_url)}
      >
        <div className={styles.cardContent}>
          <RadioButton
            inputId={doc.source_url}
            name="document"
            value={doc.source_url}
            checked={isSelected}
            onChange={(e) => onSelect(e.value)}
            className={styles.radio}
          />

          <div className={styles.cardInfo}>
            <div className={styles.cardHeader}>
              <h4 className={styles.cardTitle}>{doc.title}</h4>
              {badge && (
                <Badge
                  value={badge.label}
                  severity={badge.severity}
                  className={styles.recommendedBadge}
                />
              )}
            </div>

            <div className={styles.cardMeta}>
              <span className={styles.metaItem}>
                {getLanguageFlag(doc.language)} {getLanguageLabel(doc.language)}
              </span>
              <span className={styles.metaDivider}>•</span>
              <span className={styles.metaItem}>
                {formatDate(doc.published_date)}
              </span>
              <span className={styles.metaDivider}>•</span>
              <span className={styles.metaItem}>
                {(() => {
                  try {
                    return new URL(doc.source_url).hostname;
                  } catch {
                    return doc.source_url || 'Unknown source';
                  }
                })()}
              </span>
            </div>

            {showSimilarityScores && similarity !== undefined && (
              <div className={styles.similarityBar}>
                <div className={styles.similarityProgress}>
                  <div
                    className={styles.similarityProgressBar}
                    style={{
                      width: `${similarity * 100}%`,
                      background:
                        similarity >= 0.8
                          ? '#4caf50'
                          : similarity >= 0.6
                            ? '#2196f3'
                            : '#ff9800'
                    }}
                  />
                </div>
                <span className={styles.similarityLabel}>
                  {(similarity * 100).toFixed(0)}%
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h3 className={styles.title}>{title}</h3>
          {description && <p className={styles.description}>{description}</p>}
        </div>
        <Badge
          value={filteredAndSortedDocuments.length}
          className={styles.countBadge}
        />
      </div>

      <div className={styles.toolbar}>
        <div className={styles.searchContainer}>
          <i className="pi pi-search" />
          <InputText
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search documents..."
            className={styles.searchInput}
          />
          {searchQuery && (
            <Button
              icon="pi pi-times"
              rounded
              text
              size="small"
              onClick={() => setSearchQuery('')}
              className={styles.clearButton}
            />
          )}
        </div>

        <div className={styles.toolbarRight}>
          <Dropdown
            value={sortBy}
            options={sortOptions}
            onChange={(e) => setSortBy(e.value)}
            className={styles.sortDropdown}
            placeholder="Sort"
          />
          <Button
            icon={`pi pi-sort-${sortDirection === 'asc' ? 'amount-up' : 'amount-down'}`}
            rounded
            text
            onClick={() =>
              setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
            }
            className={styles.sortButton}
            tooltip={sortDirection === 'asc' ? 'Ascending' : 'Descending'}
            tooltipOptions={{
              position: 'top',
              showDelay: 500,
              hideDelay: 300,
              className: styles.customTooltip
            }}
          />
          <Button
            icon={viewMode === 'list' ? 'pi pi-list' : 'pi pi-th-large'}
            rounded
            text
            onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}
            className={styles.viewButton}
            tooltip={viewMode === 'list' ? 'List view' : 'Grid view'}
            tooltipOptions={{
              position: 'top',
              showDelay: 500,
              hideDelay: 300,
              className: styles.customTooltip
            }}
          />
        </div>
      </div>

      <div
        className={`${styles.documentsContainer} ${
          viewMode === 'grid' ? styles.gridView : styles.listView
        }`}
      >
        {filteredAndSortedDocuments.length === 0 ? (
          <div className={styles.emptyState}>
            <i className="pi pi-inbox" />
            <p>
              {searchQuery ? 'No documents match your search' : emptyMessage}
            </p>
          </div>
        ) : (
          <>
            {showSimilarityScores && recommendedDocs.length > 0 && (
              <>
                <div className={styles.sectionHeader}>
                  <i className="pi pi-sparkles" />
                  <span className={styles.sectionTitle}>
                    Recommended ({recommendedDocs.length})
                  </span>
                </div>
                {recommendedDocs.map(renderDocument)}
              </>
            )}

            {showSimilarityScores &&
              recommendedDocs.length > 0 &&
              otherDocs.length > 0 && (
                <div className={styles.sectionHeader}>
                  <i className="pi pi-folder" />
                  <span className={styles.sectionTitle}>
                    Other Documents ({otherDocs.length})
                  </span>
                </div>
              )}

            {showSimilarityScores && recommendedDocs.length > 0
              ? otherDocs.map(renderDocument)
              : filteredAndSortedDocuments.map(renderDocument)}
          </>
        )}
      </div>
    </div>
  );
};
